/**
 * useMarquee - A reusable marquee/slider function (no external dependencies)
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.element - CSS selector for the target element
 * @param {number} options.perView - Number of items visible at once (default: 1)
 * @param {number} options.speed - Animation speed (pixels per second) (default: 50)
 * @param {string} options.direction - Direction: 'left' or 'right' (default: 'left')
 * @param {boolean} options.pauseOnHover - Pause animation on hover (default: true)
 * @param {number} options.gap - Gap between items in pixels (default: 20)
 * @param {boolean} options.duplicate - Duplicate content for seamless loop (default: true)
 * @param {Object} options.breakpoints - Responsive breakpoints object
 * @param {number} options.breakpoints[width] - Breakpoint width in pixels
 * @param {number} options.breakpoints[width].perView - Items per view for this breakpoint
 * @returns {Object|null} - Returns marquee control object or null if element not found
 * 
 * @example
 * import useMarquee from './useMarquee.js';
 * 
 * // Basic usage
 * const marquee = useMarquee({
 *     element: '#logo-slider',
 *     perView: 6,
 *     speed: 50
 * });
 * 
 * @example
 * // Custom configuration with breakpoints
 * const marquee = useMarquee({
 *     element: '#custom-slider',
 *     perView: 4,
 *     speed: 80,
 *     direction: 'right',
 *     pauseOnHover: false,
 *     gap: 30,
 *     breakpoints: {
 *         992: {
 *             perView: 2
 *         },
 *         576: {
 *             perView: 1
 *         }
 *     }
 * });
 */
export default function useMarquee(options = {}) {
    let {
        element,
        perView = 1,
        speed = 50,
        direction = 'left',
        pauseOnHover = true,
        gap = 20,
        duplicate = true,
        breakpoints = {}
    } = options;

    // Validate required option
    if (!element) {
        console.error('useMarquee: "element" option is required');
        return null;
    }

    // Find the target element
    const container = document.querySelector(element);
    if (!container) {
        console.error(`useMarquee: Element "${element}" not found`);
        return null;
    }

    // Get the current perView based on breakpoints
    const getCurrentPerView = () => {
        const width = window.innerWidth;
        
        // Sort breakpoints by width (ascending, smallest first)
        // Keep keys as strings since object keys are strings
        const sortedBreakpoints = Object.keys(breakpoints)
            .sort((a, b) => parseInt(a) - parseInt(b));
        
        // Find the smallest breakpoint that the current width is smaller than or equal to
        // This ensures we use the most specific (smallest) matching breakpoint
        for (const bp of sortedBreakpoints) {
            if (width <= parseInt(bp) && breakpoints[bp].perView !== undefined) {
                return breakpoints[bp].perView;
            }
        }
        
        return perView;
    };

    // Ensure container has overflow hidden
    container.style.overflow = 'hidden';
    
    // Find or create track
    let track = container.querySelector('.marquee-track');
    
    if (!track) {
        // Create track wrapper with overflow hidden
        track = document.createElement('div');
        track.className = 'marquee-track';
        track.style.display = 'flex';
        track.style.whiteSpace = 'nowrap';
        
        // Get existing items
        const items = Array.from(container.children);
        
        // Move all items into the track
        items.forEach(item => {
            track.appendChild(item);
        });
        
        // Insert track into container
        container.appendChild(track);
    }

    // Get all items
    const items = Array.from(track.children);
    if (items.length === 0) {
        console.error('useMarquee: No items found in marquee');
        return null;
    }
    
    // Current perView (can change on resize)
    let currentPerView = getCurrentPerView();
    
    // Apply base styles to all items
    const applyStyles = () => {
        Array.from(track.children).forEach(item => {
            item.style.display = 'inline-flex';
            item.style.flexShrink = '0';
            item.style.marginRight = `${gap}px`;
        });
    };
    applyStyles();

    // Calculate item width
    const getItemWidth = () => {
        const containerWidth = container.offsetWidth;
        const itemWidth = containerWidth / currentPerView;
        return itemWidth;
    };

    // Store original items count before duplication
    const originalItemsCount = track.children.length;

    // Duplicate items for seamless loop
    if (duplicate) {
        const itemsToClone = Array.from(track.children);
        itemsToClone.forEach(item => {
            const clone = item.cloneNode(true);
            track.appendChild(clone);
        });
    }
    
    const updateWidths = () => {
        const itemWidth = getItemWidth();
        const allItems = Array.from(track.children);
        allItems.forEach(item => {
            item.style.width = `${itemWidth}px`;
        });
    };

    updateWidths();

    // Animation state
    let animationId = null;
    let isPaused = false;
    let currentPosition = 0;
    let lastTime = performance.now();

    // Get scroll speed in pixels per millisecond
    const getSpeed = () => {
        return speed / 1000; // Convert to pixels per millisecond
    };

    // Calculate width of original set of items
    const getOriginalSetWidth = () => {
        const itemWidth = getItemWidth();
        return originalItemsCount * (itemWidth + gap);
    };

    // Animate function
    const animate = (timestamp) => {
        if (!isPaused) {
            const delta = timestamp - lastTime;
            const scrollSpeed = getSpeed();
            
            const directionMultiplier = direction === 'left' ? -1 : 1;
            currentPosition += scrollSpeed * delta * directionMultiplier;
            
            track.style.transform = `translateX(${currentPosition}px)`;
            
            // Reset position for seamless loop
            const originalSetWidth = getOriginalSetWidth();
            if (direction === 'left' && currentPosition <= -originalSetWidth) {
                currentPosition = 0;
            } else if (direction === 'right' && currentPosition >= 0) {
                currentPosition = -originalSetWidth;
            }
        }
        
        lastTime = timestamp;
        animationId = requestAnimationFrame(animate);
    };

    // Start animation
    animationId = requestAnimationFrame(animate);

    // Mouse event handlers
    const handleMouseEnter = () => {
        if (pauseOnHover) {
            isPaused = true;
        }
    };
    
    const handleMouseLeave = () => {
        if (pauseOnHover) {
            isPaused = false;
            lastTime = performance.now();
        }
    };

    // Pause on hover
    if (pauseOnHover) {
        container.addEventListener('mouseenter', handleMouseEnter);
        container.addEventListener('mouseleave', handleMouseLeave);
    }

    // Handle window resize
    const handleResize = () => {
        const newPerView = getCurrentPerView();
        if (newPerView !== currentPerView) {
            currentPerView = newPerView;
            // Reset position on breakpoint change
            currentPosition = 0;
        }
        updateWidths();
    };
    window.addEventListener('resize', handleResize);

    // Cleanup function
    const destroy = () => {
        cancelAnimationFrame(animationId);
        window.removeEventListener('resize', handleResize);
        if (pauseOnHover) {
            container.removeEventListener('mouseenter', handleMouseEnter);
            container.removeEventListener('mouseleave', handleMouseLeave);
        }
    };

    // Pause function
    const pause = () => {
        isPaused = true;
    };

    // Play function
    const play = () => {
        isPaused = false;
        lastTime = performance.now();
    };

    // Set speed dynamically
    const setSpeed = (newSpeed) => {
        speed = newSpeed;
    };

    // Return control object
    return {
        pause,
        play,
        destroy,
        setSpeed
    };
}

